import { LocationType } from '@/entities/location';
import type { ContextGeoSubject } from '@/shared/types/geography.ts';
import { excursionFiltersToQuery, useExcursionFiltersStore } from '@/entities/filter/store/excursion';
import { useExpSelections } from '@/entities/expSelection';
import { useCityToursSelectionsExperiment } from '@/features/experiments';
import { isValidMovementType, type TMovementType, useMovementTypes } from '@/entities/movement-type';
import type { IExpSelection } from '@/entities/expSelection/model/expSelection.ts';
import type { ExpSelectionsParams } from '@/entities/expSelection/api/useExpSelection.ts';

interface ExcursionSelectionBlockProps {
  slug: string;
  location: LocationType;
  contextGeoSubject: ContextGeoSubject;
  page: number;
  isExtraSelection: boolean;
  isCityTagsCollectionsExperimentActive?: boolean;
  isInjection: boolean;
}

export const useExcursionSelectionBlock = (props: ExcursionSelectionBlockProps) => {
  const MIN_EXP_AMOUNT = 2;

  const filterStore = useExcursionFiltersStore();

  const { selectionExperimentConfig } = useCityToursSelectionsExperiment();

  const isLocation = computed(() => ({
    isRegion: props.location === LocationType.REGION,
    isCity: props.location === LocationType.CITY,
    isCountry: props.location === LocationType.COUNTRY,
  }));

  const buildPageParams = () => ({
    city: props.location === LocationType.CITY ? props.slug : undefined,
    region: props.location === LocationType.REGION ? props.slug : undefined,
    country: props.location === LocationType.COUNTRY ? props.slug : undefined,
  });

  const isRubricSelected = computed(
    () => !!filterStore.searchState.tag__slugs && filterStore.searchState.tag__slugs !== 'all',
  );
  const shouldFetchBothRequests = computed(() => !!selectionExperimentConfig.value && !isRubricSelected.value);

  const buildTagSlugs = () => {
    const isCity = isLocation.value.isCity;
    const tagSlugs = filterStore.searchState.tag__slugs;

    return isCity && tagSlugs && tagSlugs !== 'all' ? tagSlugs : '';
  };

  const buildFilterParams = (queryParams: ExpSelectionsParams) => {
    const tagSlugs = buildTagSlugs();
    if (props.isCityTagsCollectionsExperimentActive) {
      return {
        city__slug: queryParams.city__slug,
        tag__slugs: tagSlugs,
        experiment_related_tags: props.isCityTagsCollectionsExperimentActive,
      };
    }
    const hasDateFilter = !!queryParams.start_date || !!queryParams.end_date;

    return {
      ...queryParams,
      date_filter_mode: hasDateFilter ? 'event_date_range' : undefined,
      tag__slugs: tagSlugs,
      bookable: 'true',
      experiment_related_tags: props.isCityTagsCollectionsExperimentActive,
    };
  };

  const baseRequestParams = computed((): ExpSelectionsParams => {
    const pageParams = buildPageParams();
    const queryParams = excursionFiltersToQuery(filterStore.searchState, pageParams, {});
    const withType =
      shouldFetchBothRequests.value || isRubricSelected.value
        ? { ...queryParams, type: 'private,group,ticket' }
        : queryParams;

    return buildFilterParams(withType);
  });

  const tourRequestParams = computed((): ExpSelectionsParams => {
    if (!shouldFetchBothRequests.value) {
      return baseRequestParams.value;
    }
    const pageParams = buildPageParams();
    const queryParams = excursionFiltersToQuery(filterStore.searchState, pageParams, {});

    return buildFilterParams({ ...queryParams, type: 'tour' });
  });

  const { selections: baseSelections, isFetched: isBaseFetched } = useExpSelections(baseRequestParams);
  const { selections: tourSelections, isFetched: isTourFetched } = useExpSelections(tourRequestParams);

  const getSelectionByTagSlug = (selections: IExpSelection[] | undefined, tagSlug: string) =>
    selections?.find((s) => s.extra_data?.tag_slug === tagSlug);

  const resolvedSelections = computed(() => {
    if (!shouldFetchBothRequests.value) {
      return baseSelections.value;
    }

    const config = selectionExperimentConfig.value;

    if (!config) {
      return baseSelections.value;
    }

    const baseSel = getSelectionByTagSlug(baseSelections.value, config.baseTagSlug);
    const tourSel = getSelectionByTagSlug(tourSelections.value, config.tourTagSlug);

    const tagManualsFromBase = baseSelections.value?.filter((s) => s.type === 'tag_manual') ?? [];

    const includedSlugs = new Set<string>();
    for (const s of [baseSel, tourSel]) {
      if (s?.extra_data?.tag_slug) {
        includedSlugs.add(s.extra_data.tag_slug);
      }
    }

    const uniqueTagManuals = tagManualsFromBase.filter((s) => {
      const slug = s.extra_data?.tag_slug;
      if (slug && includedSlugs.has(slug)) {
        return false;
      }
      if (slug) {
        includedSlugs.add(slug);
      }

      return true;
    });

    return [...[baseSel, tourSel].filter(Boolean), ...uniqueTagManuals] as IExpSelection[];
  });

  const isFetched = computed(() =>
    shouldFetchBothRequests.value ? isBaseFetched.value && isTourFetched.value : isBaseFetched.value,
  );

  const { movementTypesExpCardLabels } = useMovementTypes();

  const selectionsWithClearedMovementType = computed(() => {
    if (!resolvedSelections.value) {
      return [] as IExpSelection[];
    }

    /**
     * в рамках эксперимента берем только выборки где больше 3-х карточек
     */
    const filteredSelections = props.isCityTagsCollectionsExperimentActive
      ? resolvedSelections.value.filter((selection) => selection.experiences.length > 3)
      : resolvedSelections.value;

    return filteredSelections.map((selection) => ({
      ...selection,
      subtitle:
        props.isCityTagsCollectionsExperimentActive && props.isInjection ? 'Посмотрите также' : selection.subtitle,
      experiences: selection.experiences.map((experience) => ({
        ...experience,
        movement_type:
          experience.movement_type && isValidMovementType(experience.movement_type)
            ? movementTypesExpCardLabels.value?.[experience.movement_type as TMovementType] || ''
            : '',
      })),
    }));
  });

  const isSelectionVisible = computed(
    () =>
      !isFetched.value ||
      selectionsWithClearedMovementType.value.some((item) => item.experiences.length > MIN_EXP_AMOUNT),
  );

  const isShowTopSelectionBlock = computed(
    () =>
      isSelectionVisible.value &&
      props.page === 1 &&
      (!filterStore.searchState.tag__slugs || filterStore.searchState.tag__slugs === 'all') &&
      (!filterStore.isFiltersApplied ||
        (props.isCityTagsCollectionsExperimentActive && filterStore.isFiltersOnlyDateApplied)),
  );

  const isShowExtraSelectionBlock = computed(
    () =>
      isSelectionVisible.value &&
      filterStore.searchState.tag__slugs &&
      filterStore.searchState.tag__slugs !== 'all' &&
      !!filterStore.searchTotalCount,
  );

  const isShowBlock = computed(() =>
    props.isExtraSelection ? isShowExtraSelectionBlock.value : isShowTopSelectionBlock.value,
  );

  return {
    isFetched,
    isLocation,
    selectionsWithClearedMovementType,
    isShowBlock,
  };
};
